/**
 * @page BugReportVerify
 * @description 2-Click-Verifikations-Seite (#943). Reporter klickt
 * "Gefixt" oder "Kaputt" — bei "Kaputt" wird Iteration erhoeht und
 * Telegram an Manuel + Claude getriggert.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle2, XCircle, ExternalLink, ArrowLeft, Bug } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

export default function BugReportVerify() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [bug, setBug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [kommentar, setKommentar] = useState('');
  const [showKommentar, setShowKommentar] = useState(false);

  useEffect(() => {
    let abort = false;
    fetch(`${API}/api/bug-reports/${id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (abort) return;
        if (j.bug) setBug(j.bug);
        else setError(j.error || 'not_found');
        setLoading(false);
      })
      .catch((e) => { if (!abort) { setError(e.message); setLoading(false); } });
    return () => { abort = true; };
  }, [id]);

  async function submit(result) {
    if (result === 'kaputt' && !showKommentar) {
      setShowKommentar(true);
      return;
    }
    if (result === 'kaputt' && !kommentar.trim()) {
      setError(t('bugReport.kommentar_required') || 'Bitte beschreibe kurz, was noch nicht funktioniert.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/bug-reports/${id}/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ result, kommentar: kommentar.trim() || null }),
      });
      const j = await res.json();
      if (!res.ok || !j.ok) {
        setError(j.error || `HTTP ${res.status}`);
        setSubmitting(false);
        return;
      }
      setDone(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 text-sm text-content-secondary">{t('common.loading') || 'Laedt ...'}</div>
    );
  }

  if (error && !bug) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-xs text-content-secondary hover:text-content mb-4 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t('common.back') || 'Zurueck'}
        </button>
        <div className="bg-rose-50 border border-rose-200 rounded-card p-4 text-sm text-rose-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-xs text-content-secondary hover:text-content mb-4 inline-flex items-center gap-1">
        <ArrowLeft size={12} /> {t('common.back') || 'Zurueck'}
      </button>

      <div className="bg-surface-card border border-line rounded-card p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
            <Bug size={18} className="text-[#338edc]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-mono text-[var(--text-tertiary)]">{bug.br_nr || `BR-${bug.id}`}</div>
            <div className="text-base font-semibold text-content">{bug.bug_titel}</div>
            <div className="text-xs text-content-secondary mt-0.5">
              {bug.schweregrad}{bug.modul ? ` · ${bug.modul}` : ''} · {t('bugReport.iteration') || 'Iteration'} {bug.iteration}
            </div>
          </div>
        </div>

        {bug.was_passiert && (
          <div>
            <div className="text-xs font-medium text-content-secondary mb-1">{t('bugReport.was_passiert_label') || 'Was passiert'}</div>
            <div className="text-sm text-content whitespace-pre-wrap">{bug.was_passiert}</div>
          </div>
        )}
        {bug.was_sollte && (
          <div>
            <div className="text-xs font-medium text-content-secondary mb-1">{t('bugReport.was_sollte_label') || 'Was sollte passieren'}</div>
            <div className="text-sm text-content whitespace-pre-wrap">{bug.was_sollte}</div>
          </div>
        )}
        {bug.fix_commit && (
          <div>
            <div className="text-xs font-medium text-content-secondary mb-1">{t('bugReport.fix_commit') || 'Fix-Commit'}</div>
            <a href={bug.fix_commit} target="_blank" rel="noreferrer" className="text-sm text-[#338edc] hover:underline inline-flex items-center gap-1">
              {bug.fix_commit} <ExternalLink size={12} />
            </a>
          </div>
        )}
        {bug.notion_url && (
          <a href={bug.notion_url} target="_blank" rel="noreferrer" className="text-xs text-[#338edc] hover:underline inline-flex items-center gap-1">
            {t('bugReport.open_notion') || 'In Notion oeffnen'} <ExternalLink size={10} />
          </a>
        )}
      </div>

      {!done ? (
        <div className="mt-5 space-y-3">
          <div className="text-sm font-medium text-content">
            {t('bugReport.verify_question') || 'Funktioniert es jetzt?'}
          </div>
          {showKommentar && (
            <div>
              <label className="block text-xs font-medium text-content-secondary mb-1">
                {t('bugReport.kommentar_label')} <span className="text-rose-500">*</span>
              </label>
              <textarea
                value={kommentar}
                onChange={(e) => setKommentar(e.target.value)}
                rows={3}
                placeholder={t('bugReport.kommentar_placeholder') || 'Was passiert jetzt anstelle des erwarteten Verhaltens?'}
                className="w-full px-3 py-2 rounded-lg border border-line bg-surface-input text-sm text-content focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 resize-none"
              />
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => submit('gefixt')}
              disabled={submitting}
              className="flex-1 px-4 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={16} /> {t('bugReport.verify_fixed') || 'Funktioniert jetzt'}
            </button>
            <button
              onClick={() => submit('kaputt')}
              disabled={submitting}
              className="flex-1 px-4 py-3 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              <XCircle size={16} /> {t('bugReport.verify_broken') || 'Immer noch kaputt'}
            </button>
          </div>
          {error && (
            <div className="text-xs text-rose-500 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>
          )}
        </div>
      ) : (
        <div className="mt-5 bg-surface-card border border-line rounded-card p-5">
          {done.verifikation === 'gefixt' ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 size={24} className="text-emerald-500 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-content">{t('bugReport.thanks_fixed') || 'Danke! Bug als gefixt markiert.'}</div>
                <div className="text-xs text-content-secondary mt-1">{t('bugReport.thanks_fixed_msg') || 'Wir haben den Status auf "Erledigt" gesetzt.'}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <XCircle size={24} className="text-rose-500 flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold text-content">
                  {done.escalated
                    ? (t('bugReport.thanks_escalated') || 'Bug eskaliert (Iteration ≥3)')
                    : (t('bugReport.thanks_broken') || 'Danke! Wir kuemmern uns nochmal drum.')}
                </div>
                <div className="text-xs text-content-secondary mt-1">
                  {t('bugReport.iteration') || 'Iteration'}: {done.iteration} · Status: {done.status}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
